// AerialCard — 鳥瞰層 diamond card, sits ABOVE the building floor stack.
//
// docs/specs/spatial-hierarchy-v2.md §6.1
//
// Visually consistent with FloorCard (45° rhombus, upright inner) so the
// user reads the whole left column as a stack: 🛰️ Site (this) → building
// header → ◇ floors. The quality badge ("衛星 / HD / 3D") tells operators
// what level of detail to expect when they click in.
//
// Click → AerialExpandedView (MapLibre satellite + location markers).
// Hover / dim semantics mirror FloorCard's phase system so the same
// state machine + animation timings drive both tiers.

import React from "react";
import { Globe2, Cuboid, Satellite } from "lucide-react";
import type { OutdoorQuality } from "../lib/visualLayersApi.js";
import { TIMINGS } from "./state/floorStackMachine.js";

export type AerialCardPhase = "idle" | "hover" | "dim" | "selected" | "expanded" | "collapsing";

interface Props {
  quality: OutdoorQuality;
  phase: AerialCardPhase;
  reduced: boolean;
  onHover: () => void;
  onUnhover: () => void;
  onClick: () => void;
}

const QUALITY_META: Record<Exclude<OutdoorQuality, "none">, { label: string; Icon: typeof Globe2; ringHex: string }> = {
  satellite_capture: { label: "衛星",   Icon: Satellite, ringHex: "#7bc7ff" },
  satellite_hd:      { label: "HD",     Icon: Satellite, ringHex: "#a8e1ff" },
  model_3d:          { label: "3D",     Icon: Cuboid,    ringHex: "#c4b5fd" },
};

export default function AerialCard({
  quality, phase, reduced, onHover, onUnhover, onClick,
}: Props): React.ReactElement | null {
  // none → render nothing; FloorStackContainer also gates on has_aerial
  // but defense-in-depth keeps the component from ever flashing a bad UI.
  if (quality === "none") return null;
  const meta = QUALITY_META[quality];

  const transform = (() => {
    if (reduced) return "none";
    switch (phase) {
      case "hover":      return "translateY(-10px) scale(1.04)";
      case "dim":        return "translateY(0px) scale(0.98)";
      case "selected":   return "translateY(-14px) scale(1.06)";
      case "expanded":   return "translateY(0px) scale(1.0)";
      case "collapsing": return "translateY(-6px) scale(1.02)";
      default:           return "translateY(0px) scale(1.0)";
    }
  })();
  const opacity = phase === "dim" ? 0.55 : 1;

  // Slightly cooler palette than FloorCard's status colours — aerial is
  // never "critical / attention" the way a floor is, so a fixed sky-cyan
  // hue tells operators "this is the geographic frame, not an operational
  // signal".
  const fill   = "rgba(96,165,250,0.12)";
  const border = "rgba(96,165,250,0.45)";
  const glow   = "rgba(96,165,250,0.30)";
  const boxShadow = `0 18px 32px -16px ${glow}, 0 0 0 1px ${border}`;

  return (
    <button
      type="button"
      data-aerial-quality={quality}
      data-aerial-phase={phase}
      onMouseEnter={onHover}
      onMouseLeave={onUnhover}
      onFocus={onHover}
      onBlur={onUnhover}
      onClick={onClick}
      aria-label={`鳥瞰層 — ${meta.label}`}
      className="aerial-card relative inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-md"
      style={{
        transform, opacity,
        transition: reduced
          ? undefined
          : `transform ${phase === "hover" || phase === "selected" ? TIMINGS.hoverInMs : TIMINGS.hoverOutMs}ms cubic-bezier(0.22,1,0.36,1), opacity ${TIMINGS.hoverInMs}ms ease-out`,
        cursor: "pointer",
      }}
    >
      <div
        className="aerial-card-rhombus relative"
        style={{
          width: "180px",
          height: "180px",
          transform: reduced ? "rotate(0deg)" : "rotate(45deg)",
          background: fill,
          boxShadow,
          borderRadius: "8px",
          border: `1px solid ${border}`,
        }}
      >
        <div
          className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center"
          style={{ transform: reduced ? "rotate(0deg)" : "rotate(-45deg)" }}
        >
          <meta.Icon className="h-7 w-7 mb-1" style={{ color: meta.ringHex }} aria-hidden />
          <span className="text-base font-mono font-semibold tracking-tight text-text-primary">鳥瞰層</span>
          <span className="mt-0.5 text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border" style={{ borderColor: border, color: meta.ringHex }}>
            {meta.label}
          </span>
          <span className="mt-1 text-[10px] text-text-muted">點擊查看衛星地圖</span>
        </div>
      </div>
    </button>
  );
}
